import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import * as moment from "moment";

// this date picker allows you to pass a string but handle the string as a date without
// having to do conversion in the parent components

class DatePickerController {
    // Two-way binding for string date (YYYY-MM-DD format)
    public ngModel: string;
    
    // Internal Date object for md-datepicker
    public dateModel: Date | null = null;
    
    // Date format is always DD/MM/YYYY (not configurable)
    private readonly dateFormat: string = 'DD/MM/YYYY';
    
    // Optional: readonly state
    public readonly: boolean = false;
    
    // Optional: name attribute
    public name: string;
    
    // Optional: aria-label
    public ariaLabel: string;
    
    // Optional: hide calendar icon
    public hideIcons: string = 'calendar';
    
    // Optional: mode - 'month' for month picker, undefined/null for day picker
    public mode: string;
    
    // Track last string value to prevent infinite loops
    private lastStringValue: string = '';
    
    /** @ngInject */
    constructor(
        private $scope: angular.IScope,
        private clientDetailsService: ClientDetailsService
    ) {}
    
    public $onInit(): void {
        // Convert initial string to Date
        if (this.ngModel) {
            let date = this.clientDetailsService.getDateObject(this.ngModel);
            // For month mode, ensure we use the first day of the month
            if (this.mode === 'month' && date) {
                date = new Date(date.getFullYear(), date.getMonth(), 1);
            }
            this.dateModel = date;
            this.lastStringValue = this.ngModel;
        }
        
        // Watch for external changes (parent updates the string)
        this.$scope.$watch(() => this.ngModel, (newValue: string) => {
            if (!newValue) {
                if (this.dateModel !== null) {
                    this.dateModel = null;
                }
                this.lastStringValue = '';
                return;
            }
            
            // Only update if the string actually changed
            if (newValue !== this.lastStringValue) {
                let newDate = this.clientDetailsService.getDateObject(newValue);
                // For month mode, ensure we use the first day of the month
                if (this.mode === 'month' && newDate) {
                    newDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
                }
                if (newDate) {
                    // Check if the date is actually different
                    if (!this.dateModel || newDate.getTime() !== this.dateModel.getTime()) {
                        this.dateModel = newDate;
                    }
                    this.lastStringValue = newValue;
                }
            }
        });
        
        // Watch for internal changes (user picks a date)
        this.$scope.$watch(() => this.dateModel, (newValue: Date | null) => {
            if (!newValue) {
                if (this.ngModel !== '') {
                    this.ngModel = '';
                    this.lastStringValue = '';
                }
                return;
            }
            
            // For month mode, use first day of the month for the string format
            let dateForString = newValue;
            if (this.mode === 'month') {
                dateForString = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
            }
            const dateString = this.clientDetailsService.getStringFromDate(dateForString);
            // Only update if the string is actually different
            if (dateString !== this.lastStringValue) {
                this.ngModel = dateString;
                this.lastStringValue = dateString;
            }
        });
    }
    
    // Format date for md-datepicker display
    // Using arrow function to preserve 'this' context when called by md-datepicker
    public formatDate = (selectedDate: Date): string => {
        if (!selectedDate) return '';
        
        // For month mode, format as "MMM YYYY" (e.g., "Sep 2025")
        if (this.mode === 'month') {
            return moment(selectedDate).format('MMM YYYY');
        }
        
        // For day mode, format as DD/MM/YYYY
        const dateOnly = moment(selectedDate).startOf('day');
        return dateOnly.format(this.dateFormat);
    }
    
    // Parse date string input to Date object for md-datepicker validation
    // Using arrow function to preserve 'this' context when called by md-datepicker
    public parseDate = (dateString: string): Date | null => {
        if (!dateString || !dateString.trim()) {
            return null;
        }
        
        // Try parsing with the expected format (DD/MM/YYYY)
        const parsed = moment(dateString.trim(), this.dateFormat, true); // strict parsing
        
        if (parsed.isValid()) {
            return parsed.toDate();
        }
        
        // If strict parsing fails, try to parse with common formats as fallback
        // This handles cases where user might type without leading zeros
        const fallbackFormats = [
            'D/M/YYYY',
            'DD/M/YYYY',
            'D/MM/YYYY',
            'DD/MM/YYYY',
            'DD-MM-YYYY',
            'D-M-YYYY'
        ];
        
        for (const fallbackFormat of fallbackFormats) {
            const fallbackParsed = moment(dateString.trim(), fallbackFormat, true);
            if (fallbackParsed.isValid()) {
                return fallbackParsed.toDate();
            }
        }
        
        // If all parsing fails, return null (will show validation error)
        return null;
    }
}

export const DatePickerComponent = {
    template: require('./date-picker.component.html'),
    style: require('./date-picker.component.scss'),
    controller: DatePickerController,
    controllerAs: '$ctrl',
    transclude: true,
    bindings: {
        ngModel: '=',        // Two-way binding for string date (YYYY-MM-DD format)
        readonly: '<?',     // Optional readonly state
        name: '@?',         // Optional name attribute
        ariaLabel: '@?',    // Optional aria-label
        hideIcons: '@?',    // Optional icons to hide (default: 'calendar')
        mode: '@?'          // Optional mode: 'month' for month picker, undefined for day picker
    }
};




// WEBPACK FOOTER //
// ./src/components/date-picker/date-picker.component.ts